import type {
  PersistedClient,
  Persister,
} from '@tanstack/query-persist-client-core'

interface PendingWrite {
  client: PersistedClient
  settled: Array<() => void>
}

export interface DisposablePersister extends Persister {
  /** Fence queued writes synchronously, then wait for any storage write already started. */
  dispose(): Promise<void>
}

/**
 * Serialize expensive full-cache writes and retain only the newest trailing
 * snapshot while a write or throttle window is active.
 */
export function createSerializedThrottledPersister(
  inner: Persister,
  throttleMs = 1500,
): DisposablePersister {
  let pendingWrite: PendingWrite | null = null
  let timer: ReturnType<typeof setTimeout> | null = null
  let inFlight: Promise<void> | null = null
  let removalPromise: Promise<void> | null = null
  let lastWriteStartedAt = 0
  let disposed = false

  const settlePendingWrite = () => {
    if (!pendingWrite) return
    pendingWrite.settled.forEach(settle => settle())
    pendingWrite = null
  }

  const schedule = () => {
    if (disposed) {
      settlePendingWrite()
      return
    }
    if (!pendingWrite || timer || inFlight || removalPromise) return

    const elapsed = Date.now() - lastWriteStartedAt
    const delay =
      lastWriteStartedAt === 0 ? 0 : Math.max(0, throttleMs - elapsed)

    if (delay > 0) {
      timer = setTimeout(() => {
        timer = null
        schedule()
      }, delay)
      return
    }

    const write = pendingWrite
    pendingWrite = null
    lastWriteStartedAt = Date.now()

    inFlight = Promise.resolve()
      .then(() => inner.persistClient(write.client))
      .catch(() => {
        console.warn('[QueryCache] Failed to persist throttled client')
      })
      .finally(() => {
        write.settled.forEach(settle => settle())
        inFlight = null
        schedule()
      })
  }

  return {
    persistClient: (client: PersistedClient) =>
      new Promise<void>(resolve => {
        if (disposed) {
          resolve()
          return
        }
        if (pendingWrite) {
          pendingWrite.client = client
          pendingWrite.settled.push(resolve)
        } else {
          pendingWrite = { client, settled: [resolve] }
        }
        schedule()
      }),
    restoreClient: async () => {
      if (disposed) return undefined
      if (removalPromise) await removalPromise
      if (inFlight) await inFlight
      if (disposed) return undefined
      const restored = await inner.restoreClient()
      return disposed ? undefined : restored
    },
    dispose: async () => {
      disposed = true
      if (timer) {
        clearTimeout(timer)
        timer = null
      }
      settlePendingWrite()
      if (inFlight) await inFlight
    },
    removeClient: () => {
      if (removalPromise) return removalPromise

      if (timer) {
        clearTimeout(timer)
        timer = null
      }
      // A queued snapshot predates the removal and must not recreate the cache.
      settlePendingWrite()

      const activeWrite = inFlight
      removalPromise = (async () => {
        if (activeWrite) await activeWrite
        await inner.removeClient()
      })().finally(() => {
        removalPromise = null
        lastWriteStartedAt = 0
        // Writes received while removal was in progress belong to the new cache.
        schedule()
      })

      return removalPromise
    },
  }
}
